import MnemonicTable from "@features/graph/details/components/MnemonicTable"
import {
  getScenarioColumnIndex,
  getUniqueScenarios,
  mergeMnemonicDataWithEmbeddedScenario,
} from "@features/graph/details/components/MnemonicTable/helpers"
import ScenariosTabs from "@features/graph/details/components/MnemonicTable/ScenariosTabs"
import { useLocalGraphScenario } from "@hooks/useLocalGraphScenario"
import { ExpandMore } from "@mui/icons-material"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material"
import { useGetGraphForecastMnemonicQuery } from "@store/services/graphsService"
import { getTableRowsFromCsvContent } from "@utils/file"
import { useEffect, useMemo, useState } from "react"
import { Node } from "reactflow"
import { useLocalStorage } from "usehooks-ts"

type ScanariosAccordionProps = {
  graphKey?: string
  editable: boolean
  node: Node
}

const ScanariosAccordion = (props: ScanariosAccordionProps) => {
  const [isExpanded, setIsExpanded] = useLocalStorage(`scenariosAccordionExpanded`, true)
  const [scenarioTab, setScenarioTab] = useState("")

  const graphKey = props.graphKey ?? ""
  const mnemonic = props.node.data.display["Scenario Mnem"]
  const { scenario, updateScenario } = useLocalGraphScenario(graphKey)
  const getMnemonicQuery = useGetGraphForecastMnemonicQuery(
    {
      key: graphKey,
      mnemonic,
      isScenario: true,
    },
    { skip: !graphKey },
  )

  const scenarios = useMemo(
    () => (getMnemonicQuery.data ? getUniqueScenarios(getMnemonicQuery.data) : []),
    [getMnemonicQuery.data],
  )

  useEffect(() => {
    const firstScenario = scenarios[0]
    if (!firstScenario) return

    setScenarioTab(firstScenario)
  }, [scenarios])

  if (getMnemonicQuery.isLoading) {
    return (
      <Grid container justifyContent="center" sx={{ p: 1 }}>
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  if (!getMnemonicQuery.data || getMnemonicQuery.data.empty) return <></>

  // rows for this mnemonic, read from the live (locally edited) scenario CSV rather than the
  // node's data.scenario snapshot, so the table reflects edits immediately
  const embeddedScenarioRows = scenario
    ? getTableRowsFromCsvContent(scenario).filter((row) => row.Mnem === mnemonic)
    : props.node.data.scenario

  // filter out rows that are not related to our selected
  const scenarioIndex = getScenarioColumnIndex(getMnemonicQuery.data)
  const filteredData = mergeMnemonicDataWithEmbeddedScenario(
    getMnemonicQuery.data,
    embeddedScenarioRows,
  )
  filteredData.rows = filteredData.rows.filter((row) => row[scenarioIndex] === scenarioTab)

  const handleEditMnem = async (timestep: string, newValue: string) => {
    await updateScenario({
      Date: timestep,
      Value: newValue,
      Mnem: mnemonic,
      Scenario: scenarioTab,
    })
  }

  return (
    <Grid item sx={{ width: isExpanded ? "auto" : "100%" }}>
      <Accordion expanded={isExpanded} onChange={(_event, newValue) => setIsExpanded(newValue)}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography sx={{ fontWeight: 700 }}>Scenario Data</Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          {scenarioTab && (
            <ScenariosTabs
              scenarios={scenarios}
              onChange={setScenarioTab}
              selectedScenario={scenarioTab}
            />
          )}
          <MnemonicTable data={filteredData} editable={props.editable} onEdit={handleEditMnem} />
        </AccordionDetails>
      </Accordion>
    </Grid>
  )
}

export default ScanariosAccordion
